import { FileText, Eye, Users2, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { StatCard, ModulePanel, DataTable, EditorialStatusBadge } from "@/components/admin/AdminPrimitives";
import { useMesArticles } from "@/hooks/useArticles";
import { useMyAnalyticsSummary } from "@/hooks/useAnalytics";
import { STATUT_MAP } from "@/lib/enums";
import { useI18n } from "@/lib/i18n";
import type { ArticleDto } from "@/types/article.types";

/** Dashboard réservé aux comptes qui n'ont que le rôle REDACTEUR : leurs propres
 * articles (avec statut), et leurs statistiques d'audience agrégées. */
function DashboardRedacteur() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data: articlesData, isLoading } = useMesArticles();
  const { data: audience, isLoading: loadingAudience } = useMyAnalyticsSummary();

  const articles: ArticleDto[] = Array.isArray(articlesData) ? articlesData : [];

  const compte = (statut: string) => articles.filter((a) => a.statut === statut).length;

  return (
    <>
      <AdminTopbar title="Mon tableau de bord" subtitle="Vos articles et leur audience" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label="Publiés" value={compte("PUBLIE")} icon={Send} accent="verified" />
          <StatCard label="En attente" value={compte("BROUILLON")} icon={FileText} />
          <StatCard label="Relecture" value={compte("RELECTURE")} icon={FileText} />
          <StatCard label="Refusés" value={compte("REFUSE")} icon={FileText} accent="alert" />
          <StatCard label="Total" value={articles.length} icon={FileText} />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Vues"
            value={loadingAudience ? "…" : audience?.vues ?? 0}
            icon={Eye}
          />
          <StatCard
            label="Lectures"
            value={loadingAudience ? "…" : audience?.lectures ?? 0}
            icon={FileText}
          />
          <StatCard
            label="Visiteurs uniques"
            value={loadingAudience ? "…" : audience?.visiteursUniques ?? 0}
            icon={Users2}
          />
        </div>

        <ModulePanel title={`Mes articles (${articles.length})`}>
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">{t("admin.common.loading")}</div>
          ) : articles.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Vous n'avez pas encore d'article. Créez-en un depuis "Articles".
            </div>
          ) : (
            <DataTable
              columns={["Titre", "Statut", "Date"]}
              rows={articles.map((a) => [
                <button
                  onClick={() => navigate(`/redaction/articles/${a.id}`)}
                  className="text-left font-medium text-foreground hover:underline"
                >
                  {a.traductions?.[0]?.titre || a.slug}
                </button>,
                <EditorialStatusBadge status={STATUT_MAP[a.statut] || a.statut} />,
                a.modifieLe || a.creeLe ? new Date(a.modifieLe || a.creeLe!).toLocaleDateString("fr-FR") : "—",
              ])}
            />
          )}
        </ModulePanel>
      </div>
    </>
  );
}

export default DashboardRedacteur;
